import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
    Box,
    Button,
    ColumnLayout,
    Container,
    Grid,
    Header,
    Link,
    SpaceBetween,
} from "@cloudscape-design/components";
import { setPageTitle } from "src/core/redux";
import { SentinelPageProps } from "src/web/interfaces";
import {
    CREATE_ISSUE_URL,
    SLACK_CHANNEL,
    SLACK_URL,
    WIKI_URL,
} from "src/web/constants";
import { DESTINATIONS, STEPS } from "./constants";
import { useNavigate } from "react-router-dom";
import "../../styles/app.css";
import dsLogo from "!!url-loader!src/assets/ds-logo.png";

export interface SentinelOverviewPageProps extends SentinelPageProps { }

export const LandingPage: React.FC<SentinelOverviewPageProps> = (
    // eslint-disable-next-line
    props: SentinelOverviewPageProps
) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setPageTitle("Overview"));
    }, []);

    return (
        <SpaceBetween size="l">
            <Container>
                <Grid
                    gridDefinition={[
                        { colspan: { default: 12, s: 4 } },
                        { colspan: { default: 12, s: 8 } },
                    ]}
                >
                    <Box textAlign="center">
                        <img
                            src={dsLogo}
                            width={220}
                            height={220}
                            alt="Dependency Sentinel"
                            style={{ maxWidth: "100%", height: "auto" }}
                        />
                    </Box>
                    <SpaceBetween size="m">
                        <Box variant="h1">Keep your dependencies current, automatically</Box>
                        <Box variant="p" fontSize="heading-xs" color="text-body-secondary">
                            <span className="bolded">Dependency Sentinel</span> watches the
                            packages your pipelines build, tracks vendor guidance for every
                            dependency, and raises code reviews. You review and merge; nothing lands without
                            your approval.
                        </Box>
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="primary" onClick={() => navigate("onboard")}>
                                Onboard Now
                            </Button>
                            <Button onClick={() => navigate("dry-run")}>
                                Try Dry Run Mode
                            </Button>
                        </SpaceBetween>
                    </SpaceBetween>
                </Grid>
            </Container>

            <Container
                header={
                    <Header
                        variant="h2"
                        description="From an onboarded pipeline to a merged upgrade, in five steps."
                    >
                        How it works
                    </Header>
                }
            >
                <ColumnLayout columns={2}>
                    {STEPS.map((step, index) => (
                        <div key={step.title}>
                            <Box variant="awsui-key-label">Step {index + 1}</Box>
                            <Box variant="h4" padding={{ top: "n", bottom: "xxs" }}>
                                {step.title}
                            </Box>
                            <Box variant="p" color="text-body-secondary">
                                {step.detail}
                            </Box>
                        </div>
                    ))}
                </ColumnLayout>
            </Container>

            <Container
                header={<Header variant="h2">Explore</Header>}
            >
                <ColumnLayout columns={3}>
                    {DESTINATIONS.map((destination) => (
                        <div key={destination.title}>
                            <Box variant="h4" padding={{ top: "n", bottom: "xxs" }}>
                                <Link href={destination.href}>{destination.title}</Link>
                            </Box>
                            <Box variant="p" color="text-body-secondary">
                                {destination.detail}
                            </Box>
                        </div>
                    ))}
                </ColumnLayout>
            </Container>

            <Container
                header={
                    <Header
                        variant="h2"
                        description="Questions, feature requests, or something that looks wrong."
                    >
                        Help and community
                    </Header>
                }
            >
                <ColumnLayout columns={3}>
                    <div>
                        <Box variant="h4" padding={{ top: "n", bottom: "xxs" }}>
                            <Link href={SLACK_URL} external={true}>
                                Slack
                            </Link>
                        </Box>
                        <Box variant="p" color="text-body-secondary">
                            Join {SLACK_CHANNEL} to ask the team anything and hear about what is
                            shipping next.
                        </Box>
                    </div>
                    <div>
                        <Box variant="h4" padding={{ top: "n", bottom: "xxs" }}>
                            <Link href={WIKI_URL} external={true}>
                                Wiki
                            </Link>
                        </Box>
                        <Box variant="p" color="text-body-secondary">
                            Design documents, FAQ, and the user guide for Dependency Sentinel.
                        </Box>
                    </div>
                    <div>
                        <Box variant="h4" padding={{ top: "n", bottom: "xxs" }}>
                            <Link href={CREATE_ISSUE_URL} external={true}>
                                Create Issue
                            </Link>
                        </Box>
                        <Box variant="p" color="text-body-secondary">
                            Cut a ticket to the team for a bug or a
                            request.
                        </Box>
                    </div>
                </ColumnLayout>
            </Container>
        </SpaceBetween>
    );
};

export default LandingPage;
